import React from 'react';
import { Box, Button, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import DepartmentSelect from '../Departments/DepartmentSelect';
import { EMPTY_FILTERS, STAGE_OPTIONS, STATUS_OPTIONS, hasActiveFilters } from '../../utils/listFilterParams';
import { CONTRACT_OPTIONS, COMPLETION_OPTIONS } from '../../utils/contractApplicability';

const fontBase = { fontFamily: 'Roboto, Arial, sans-serif' };
const control = { minWidth: 180, '& .MuiOutlinedInput-root': { backgroundColor: '#fff', borderRadius: '8px', fontSize: '14px' } };

/**
 * The filter bar the Suppliers and All Tasks lists share: status, stage,
 * department, contract applicability, completion and a submitted-date range.
 *
 * A management user's department is pinned — the server scopes them to it
 * regardless, so offering the choice would only show them empty lists.
 */
const ListFilters = ({ filters, onChange, pinnedDepartment = null }) => {
  const set = (key) => (value) => onChange({ ...filters, [key]: value });
  const active = hasActiveFilters({ ...filters, department: pinnedDepartment ? '' : filters.department });

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
      <FormControl size="small" sx={control}>
        <InputLabel sx={{ ...fontBase, fontSize: '14px' }}>Status</InputLabel>
        <Select label="Status" value={filters.status} onChange={(e) => set('status')(e.target.value)}>
          <MenuItem value="">All statuses</MenuItem>
          {STATUS_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ ...control, minWidth: 220 }}>
        <InputLabel sx={{ ...fontBase, fontSize: '14px' }}>Stage</InputLabel>
        <Select label="Stage" value={filters.stage} onChange={(e) => set('stage')(e.target.value)}>
          <MenuItem value="">All stages</MenuItem>
          {STAGE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </Select>
      </FormControl>

      <Box sx={{ minWidth: 180 }}>
        <DepartmentSelect
          value={pinnedDepartment ?? filters.department}
          onChange={set('department')}
          label="Department"
          includeAll={!pinnedDepartment}
          disabled={Boolean(pinnedDepartment)}
        />
      </Box>

      <FormControl size="small" sx={{ ...control, minWidth: 210 }}>
        <InputLabel sx={{ ...fontBase, fontSize: '14px' }}>Contract</InputLabel>
        <Select label="Contract" value={filters.contract} onChange={(e) => set('contract')(e.target.value)}>
          <MenuItem value="">Any</MenuItem>
          {CONTRACT_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ ...control, minWidth: 160 }}>
        <InputLabel sx={{ ...fontBase, fontSize: '14px' }}>Completion</InputLabel>
        <Select label="Completion" value={filters.completion} onChange={(e) => set('completion')(e.target.value)}>
          <MenuItem value="">Any</MenuItem>
          {COMPLETION_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </Select>
      </FormControl>

      <TextField
        size="small"
        type="date"
        label="Submitted from"
        InputLabelProps={{ shrink: true }}
        value={filters.submittedFrom}
        onChange={(e) => set('submittedFrom')(e.target.value)}
        inputProps={{ max: filters.submittedTo || undefined }}
        sx={{ ...control, minWidth: 160 }}
      />
      <TextField
        size="small"
        type="date"
        label="Submitted to"
        InputLabelProps={{ shrink: true }}
        value={filters.submittedTo}
        onChange={(e) => set('submittedTo')(e.target.value)}
        inputProps={{ min: filters.submittedFrom || undefined }}
        sx={{ ...control, minWidth: 160 }}
      />

      {active && (
        <Button
          size="small"
          onClick={() => onChange(EMPTY_FILTERS)}
          sx={{ ...fontBase, textTransform: 'none', color: '#6b7280', fontSize: '14px' }}
        >
          Clear filters
        </Button>
      )}
    </Box>
  );
};

export default ListFilters;
